/**
 * DOVE-2298 — typed contract for `GET /api/v1/get-available-driver`
 * (backend `DriverTruckAssignmentResource`).
 *
 * Every call site used to request `any[]`, so when DOVE-2295 widened the roster to include
 * truckless 3rd-party/subcontractor assignments — making `truck`, `truck_name`, `truck_id` and
 * the truck-derived `branch` all nullable — TypeScript could not flag a single one of the four
 * unguarded dereferences and the change surfaced only as a runtime TypeError. Keeping the
 * nullability faithful here is what makes the compiler catch the next such change.
 */

export interface AvailableDriverUserResponse {
	id: number;
	/** `users.firstname` / `users.lastname` are NOT NULL, so these are safe to read unguarded. */
	firstname: string;
	middlename: string | null;
	lastname: string;
	fullname: string | null;
	name: string;
	email: string | null;
	active: number | boolean | null;
	phone_number: string | null;
	branch_id: number | null;
	branch: string;
	user_type_id: number | null;
	user_type: string;
	startdate: string | null;
}

export interface AvailableDriverBranchResponse {
	id: number;
	name: string | null;
}

export interface AvailableDriverTruckResponse {
	id: number;
	name: string | null;
	branch_id: number | null;
	branch_name: string | null;
	license_plate: string | null;
	vin: string | null;
	driver: string | null;
	active: number | boolean | null;
}

export interface AvailableDriverResponse {
	driver_truck_id: number;
	user_id: number;
	driver: AvailableDriverUserResponse | null;
	truck_id: number | null;
	truck_name: string | null;
	truck: AvailableDriverTruckResponse | null;
	/**
	 * `$truckInfo?->branch_id ?? $this->branch_id`. Populated for both trucked and truckless rows
	 * in practice, but the underlying column is nullable, so it stays optional here.
	 */
	branch_id: number | null;
	/** Resolved through the truck, therefore always null on a truckless assignment. */
	branch: AvailableDriverBranchResponse | null;
}

/** Normalized shape consumed by the driver dropdowns. */
export interface AvailableDriverOption {
	value: number;
	label: string;
	userId: number;
	driverTruckId: number;
	truckId: number | null;
	truckName: string | null;
	branchId: number | null;
	branchName: string | null;
}

/** A roster row already narrowed to one that carries a driver. */
export type AssignedAvailableDriverResponse = AvailableDriverResponse & { driver: AvailableDriverUserResponse };

export function hasDriver(assignment: AvailableDriverResponse): assignment is AssignedAvailableDriverResponse {
	return !!assignment?.driver;
}

/**
 * Single place that knows how to flatten a roster row. Truck- and branch-derived fields collapse
 * to null rather than to the string "null", so a truckless row can still render in a dropdown.
 */
export function toAvailableDriverOption(assignment: AssignedAvailableDriverResponse): AvailableDriverOption {
	const driver = assignment.driver;

	// The API builds `name` as first + middle + last, so a null middle name leaves a double space.
	const label = (driver.name || `${driver.firstname} ${driver.lastname}`).replace(/\s+/g, ' ').trim();

	return {
		value: assignment.driver_truck_id,
		label,
		userId: driver.id,
		driverTruckId: assignment.driver_truck_id,
		truckId: assignment.truck_id ?? null,
		truckName: assignment.truck_name ?? assignment.truck?.name ?? null,
		branchId: assignment.branch?.id ?? assignment.branch_id ?? null,
		branchName: assignment.branch?.name ?? assignment.truck?.branch_name ?? null
	};
}

export function toAvailableDriverOptions(assignments: AvailableDriverResponse[]): AvailableDriverOption[] {
	return assignments.filter(hasDriver).map(toAvailableDriverOption);
}

/**
 * True when `candidate` should replace `incumbent` as the surviving row for a driver.
 *
 * Deliberately independent of the order the API happened to return the rows in:
 *   1. a trucked assignment beats a truckless one — it carries the real branch object plus a
 *      truck name, so the survivor is the row with the most information;
 *   2. otherwise the higher driver_truck_id wins, i.e. the most recently created assignment.
 */
function outranksForSameUser(candidate: AvailableDriverOption, incumbent: AvailableDriverOption): boolean {
	const candidateHasTruck = candidate.truckId !== null;
	const incumbentHasTruck = incumbent.truckId !== null;

	if (candidateHasTruck !== incumbentHasTruck) {
		return candidateHasTruck;
	}

	return candidate.driverTruckId > incumbent.driverTruckId;
}

/**
 * DOVE-2298 — `get-available-driver` returns one row per driver_truck_assignment, so a driver
 * holding several assignments for the same branch and date is listed several times (order 3353610,
 * branch 1, 2026-08-14: 39 rows for 34 distinct people, one driver appearing three times).
 *
 * Only collapse the list where the dropdown is keyed on the *person*. A dropdown keyed on the
 * assignment — the dispatcher board, which looks orders up by driver_truck_assignment_id — must
 * keep every row, or an order assigned to the discarded assignment can no longer resolve its
 * driver. That is why this is a separate helper instead of being folded into
 * `toAvailableDriverOptions()`.
 *
 * Sorted by label so the result reads cleanly once a filter box is put in front of it.
 */
export function dedupeByUser(options: AvailableDriverOption[]): AvailableDriverOption[] {
	const byUserId = new Map<number, AvailableDriverOption>();

	for (const option of options) {
		const incumbent = byUserId.get(option.userId);

		if (!incumbent || outranksForSameUser(option, incumbent)) {
			byUserId.set(option.userId, option);
		}
	}

	return [...byUserId.values()].sort((a, b) => a.label.localeCompare(b.label));
}
